import React, { useEffect, useState, useCallback } from "react";
import { apiGet, apiPost, apiDelete } from "../lib/api";
import QuestionView from "../components/QuestionView";
import FreeTrialBanner from "../components/FreeTrialBanner";
import MotionButton from "../components/motion/MotionButton";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "../components/ui/select";
import { Loader2, BookOpen } from "lucide-react";

export default function Training() {
  const [specialty, setSpecialty] = useState("all");
  const [difficulty, setDifficulty] = useState("all");
  const [specialties, setSpecialties] = useState([]);
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [favIds, setFavIds] = useState(new Set());

  useEffect(() => {
    apiGet("/specialties").then((r) => setSpecialties(r.specialties || []));
    apiGet("/favorites").then((items) => setFavIds(new Set(items.map((i) => i.question_id))));
  }, []);

  const loadNext = useCallback(async () => {
    setLoading(true);
    try {
      const params = { count: 1 };
      if (specialty !== "all") params.specialty = specialty;
      if (difficulty !== "all") params.difficulty = difficulty;
      const r = await apiGet("/questions/random", params);
      setQuestion(r[0] || null);
    } finally {
      setLoading(false);
    }
  }, [specialty, difficulty]);

  const toggleFav = async (qid, want) => {
    if (want) {
      await apiPost(`/favorites/${qid}`);
      setFavIds((prev) => new Set([...prev, qid]));
    } else {
      await apiDelete(`/favorites/${qid}`);
      setFavIds((prev) => {
        const n = new Set(prev);
        n.delete(qid);
        return n;
      });
    }
  };

  const btnLabel = (() => {
    if (loading) return <><Loader2 className="h-4 w-4 animate-spin" /> Caricamento</>;
    if (question) return "Nuova domanda";
    return "Inizia";
  })();

  return (
    <div className="px-6 sm:px-10 py-8 lg:py-12 max-w-4xl mx-auto">
      <div className="mb-7">
        <div className="eyebrow">// Modalità</div>
        <h1 className="heading-display text-4xl sm:text-5xl text-brand-primary mt-1 flex items-center gap-3">
          <BookOpen className="h-8 w-8 text-brand-green" />
          Allenamento
        </h1>
        <p className="text-base text-brand-secondary mt-2">
          Pratica libera con feedback immediato dopo ogni risposta.
        </p>
      </div>

      <FreeTrialBanner />

      <div className="premium-card p-5 mb-6 grid sm:grid-cols-3 gap-4">
        <div>
          <label className="eyebrow mb-2 block">Specialità</label>
          <Select value={specialty} onValueChange={setSpecialty}>
            <SelectTrigger data-testid="filter-specialty" className="bg-brand-background border-brand-border rounded-xl h-11">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte</SelectItem>
              {specialties.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="eyebrow mb-2 block">Difficoltà</label>
          <Select value={difficulty} onValueChange={setDifficulty}>
            <SelectTrigger data-testid="filter-difficulty" className="bg-brand-background border-brand-border rounded-xl h-11">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte</SelectItem>
              <SelectItem value="easy">Facile</SelectItem>
              <SelectItem value="medium">Media</SelectItem>
              <SelectItem value="hard">Difficile</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <MotionButton
            data-testid="start-training-btn"
            variant="primary"
            size="md"
            className="w-full"
            onClick={loadNext}
            disabled={loading}
          >
            {btnLabel}
          </MotionButton>
        </div>
      </div>

      {question ? (
        <QuestionView
          question={question}
          mode="training"
          isFavorite={favIds.has(question.question_id)}
          onToggleFavorite={toggleFav}
          onNext={loadNext}
        />
      ) : (
        <div className="border-2 border-dashed border-brand-border rounded-2xl p-12 text-center text-brand-secondary bg-brand-surface">
          Seleziona i filtri e premi <span className="text-brand-primary font-medium">Inizia</span> per cominciare.
        </div>
      )}
    </div>
  );
}
